// IMPORTANT: Email confirmation MUST be enabled in the Supabase Dashboard:
//   Authentication > Sign In / Providers > Email > "Confirm email" = ON
// Confirmation + recovery emails are delivered via Custom SMTP (Resend),
// configured at: Authentication > Emails > SMTP Settings.
// This blocks fake signups and ensures we can reach paying customers.
// DO NOT disable email confirmation in production.
import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = 'https://jpxmzqasmtmyxhtgisfu.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImpweG16cWFzbXRteXhodGdpc2Z1Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzYyMTAzMTAsImV4cCI6MjA5MTc4NjMxMH0.vPw-fdACgWHrn6kiSaPpq-WbqJS5gs30WEJirpERexE';

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── AUTH FUNCTIONS ────────────────────────────────────────────
export async function signUp(email, password, name, extra = {}) {
  const { data, error } = await supabase.auth.signUp({
    email, password,
    options: { data: { full_name: name, ...extra } }
  });
  if (error) throw error;
  return data;
}

export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signInWithGoogle() {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin }
  });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function resetPassword(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`
  });
  if (error) throw error;
}

export async function resetPasswordForEmail(email) {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: 'https://rn101hq.com/reset-password',
  });
  if (error) throw error;
  return data;
}

export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

export function onAuthChange(callback) {
  return supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session?.user ?? null);
  });
}

// ── DATABASE FUNCTIONS ────────────────────────────────────────
export async function getUserProfile(userId) {
  const { data, error } = await supabase
    .from('profiles').select('*').eq('id', userId).single();
  if (error) throw error;
  return data;
}

export async function incrementSessions(userId) {
  const { error } = await supabase.rpc('increment_sessions', { user_id: userId });
  if (error) throw error;
}

export async function updateUserPlan(userId, plan) {
  const { error } = await supabase
    .from('profiles')
    .update({ plan, sessions_used: 0, updated_at: new Date() })
    .eq('id', userId);
  if (error) throw error;
}

export async function saveResponse({ userId, toolId, toolTitle, question, response }) {
  const { data, error } = await supabase
    .from('saved_responses')
    .insert({ user_id: userId, tool_id: toolId, tool_title: toolTitle, question, response })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getSavedResponses(userId) {
  const { data, error } = await supabase
    .from('saved_responses')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;
  return data;
}

export async function deleteSavedResponse(id) {
  const { error } = await supabase
    .from('saved_responses')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

export async function updatePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
}
